/**
 * Single source of truth for converting a customer-entered ZAR amount into
 * a credit count + total charge. Used by both client UI and server
 * validation so the two NEVER drift.
 *
 * Rules (mirror server-side checks in `orderCreditPack`):
 *   • Customer enters a target spend in ZAR (already includes admin fee).
 *   • Minimum total = MIN_CREDIT_SPEND_ZAR + ADMIN_FEE_ZAR.
 *   • Credit count = ceil((entered - admin) / unit) so we never short the
 *     server's minimum-credits check (which also uses ceil).
 *   • Total charge is then recomputed as credits * unit + admin so the
 *     PayFast amount always matches the credits granted, to the cent.
 */
import {
  ADMIN_FEE_ZAR,
  MIN_CREDIT_SPEND_ZAR,
  PRICE_PER_CREDIT_ZAR,
} from "./credit-packs";

export type CreditBreakdown = {
  /** Raw amount the customer typed, in ZAR. */
  enteredAmount: number;
  /** Whether `enteredAmount` meets the R30 minimum. */
  meetsMinimum: boolean;
  /** Smallest credit count the server will accept (ceil of min/unit). */
  minCredits: number;
  /** Credit count we'll actually order (≥ minCredits when valid). */
  credits: number;
  /** Portion attributed to credits (credits * unit), 2dp. */
  creditsValueZar: number;
  /** Fixed admin fee in ZAR. */
  adminFeeZar: number;
  /** Final total charged at PayFast (credits + admin), 2dp. */
  totalZar: number;
  /** ZAR per credit constant. */
  unitPriceZar: number;
  /** Minimum total charge constant (credits min + admin). */
  minTotalZar: number;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

export function computeCreditBreakdown(enteredAmountZar: number): CreditBreakdown {
  const minCredits = Math.ceil(MIN_CREDIT_SPEND_ZAR / PRICE_PER_CREDIT_ZAR);
  const minTotalZar = round2(MIN_CREDIT_SPEND_ZAR + ADMIN_FEE_ZAR);
  const safe = Number.isFinite(enteredAmountZar) && enteredAmountZar > 0
    ? enteredAmountZar
    : 0;
  const meetsMinimum = safe >= minTotalZar - 0.0001;

  if (!meetsMinimum) {
    return {
      enteredAmount: safe,
      meetsMinimum: false,
      minCredits,
      credits: 0,
      creditsValueZar: 0,
      adminFeeZar: ADMIN_FEE_ZAR,
      totalZar: 0,
      unitPriceZar: PRICE_PER_CREDIT_ZAR,
      minTotalZar,
    };
  }

  const spendOnCredits = Math.max(0, safe - ADMIN_FEE_ZAR);
  const credits = Math.max(
    minCredits,
    Math.ceil(spendOnCredits / PRICE_PER_CREDIT_ZAR),
  );
  const creditsValueZar = round2(credits * PRICE_PER_CREDIT_ZAR);
  const totalZar = round2(creditsValueZar + ADMIN_FEE_ZAR);

  return {
    enteredAmount: safe,
    meetsMinimum: true,
    minCredits,
    credits,
    creditsValueZar,
    adminFeeZar: ADMIN_FEE_ZAR,
    totalZar,
    unitPriceZar: PRICE_PER_CREDIT_ZAR,
    minTotalZar,
  };
}
